/**
 * NPA Analysis — most analytically-rich page (PHASE2_SPEC §4.4).
 *
 * Layout:
 *   KPI tiles (NPA ₹Cr · system NPA % · banks > RBI · banks in red zone)
 *   Bar chart: NPA category breakdown system-wide
 *   Per-bank table (sortable, default by NPA % desc)
 *   Top product NPA contributors
 */

import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import BankBadge from '../../components/BankBadge';
import BarChart from '../../components/BarChart';
import Card from '../../components/Card';
import EmptyState from '../../components/EmptyState';
import ErrorBanner from '../../components/ErrorBanner';
import KpiTile from '../../components/KpiTile';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import SortableTh from '../../components/SortableTh';
import StatusPill from '../../components/StatusPill';
import { formatDate } from '../../helpers/dateFormat';
import { npaFlag } from '../../helpers/flag';
import { formatAbsCrore, formatCount, formatCrore, formatPct } from '../../helpers/format';
import { NPA_COLORS } from '../../helpers/palette';
import { useSortable } from '../../hooks/useSortable';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchNPA, selectNPA } from '../../redux/slices/dashboard';
import type { NPAByBankRow, NPAByProductRow } from '../../type/api';

const CATEGORY_COLOR: Record<string, string> = {
	Standard: NPA_COLORS.standard,
	'Sub-Standard': NPA_COLORS.subStandard,
	Doubtful: NPA_COLORS.doubtful,
	Loss: NPA_COLORS.loss,
};

export default function NPAAnalysisPage() {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const cell = useAppSelector(selectNPA);

	useEffect(() => {
		if (cell.status === 'idle') {
			dispatch(fetchNPA());
		}
	}, [cell.status, dispatch]);

	const data = cell.data;
	const byBank: NPAByBankRow[] = useMemo(
		() => (Array.isArray(data?.byBank) ? data!.byBank : []),
		[data],
	);
	const byProduct: NPAByProductRow[] = useMemo(
		() => (Array.isArray(data?.byProduct) ? data!.byProduct : []),
		[data],
	);

	const bankSort = useSortable<NPAByBankRow>(byBank, { field: 'npaPct', dir: 'desc' });
	const productSort = useSortable<NPAByProductRow>(byProduct, { field: 'npaCr', dir: 'desc' });

	const handleRetry = () => dispatch(fetchNPA());

	if (cell.status === 'loading' && !data) {
		return (
			<div className="page-body">
				<div className="page-head"><div><h1 className="page-title">NPA Analysis</h1></div></div>
				<Card><LoadingSkeleton rows={6} columns={6} /></Card>
			</div>
		);
	}
	if (cell.status === 'error') {
		return (
			<div className="page-body">
				<div className="page-head"><div><h1 className="page-title">NPA Analysis</h1></div></div>
				<ErrorBanner message={cell.error || 'Failed to load NPA'} onRetry={handleRetry} />
			</div>
		);
	}
	if (!data) {
		return (
			<div className="page-body">
				<div className="page-head"><div><h1 className="page-title">NPA Analysis</h1></div></div>
				<Card><EmptyState title="No NPA data" hint="Once the ETL completes a snapshot, this page will populate." /></Card>
			</div>
		);
	}

	const summary = data.summary;
	const classifications = Array.isArray(data.classifications) ? data.classifications : [];
	const categoryBars = classifications.map((c) => ({
		name: c.code,
		value: Math.abs(c.loanCr),
		color: CATEGORY_COLOR[c.category] || 'var(--navy-500)',
	}));

	return (
		<div className="page-body">
			<div className="page-head">
				<div>
					<h1 className="page-title">NPA Analysis</h1>
					<div className="page-sub">
						System-wide non-performing assets.
						{data.asOf && (
							<>
								{' · As of '}
								<span style={{ color: 'var(--text-dim)', fontWeight: 500 }}>
									{formatDate(data.asOf)}
								</span>
							</>
						)}
					</div>
				</div>
				<button type="button" className="btn ghost" onClick={handleRetry}>Refresh</button>
			</div>

			<div className="grid cols-4">
				<KpiTile label="Total NPA" value={formatCrore(summary.totalNpaCr, 2)} sub={`${formatCount(summary.totalNpaCount)} accounts`} />
				<KpiTile
					label="System NPA %"
					value={formatPct(summary.systemNpaPct)}
					flag={npaFlag(summary.systemNpaPct)}
					flagVariant="npa"
					primary
				/>
				<KpiTile label="Banks > RBI norm (>7%)" value={formatCount(summary.banksAboveRBI)} sub="Above 7%" />
				<KpiTile label="Banks in Red Zone" value={formatCount(summary.banksAtRisk)} sub="Above 10%" flag="RED" flagVariant="npa" />
			</div>

			<Card title="NPA classification (₹ Cr)">
				<BarChart data={categoryBars} valueFormatter={(v) => formatCrore(v, 1)} yAxisLabel="₹ Cr" />
				<div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
					{classifications.map((c) => (
						<span key={c.code} style={{ fontSize: 12, color: 'var(--text-dim)' }}>
							<b style={{ color: 'var(--text)' }}>{c.code}</b> {c.label} · {formatCount(c.loanCount)} acct ·{' '}
							{formatAbsCrore(c.loanCr)}
						</span>
					))}
				</div>
			</Card>

			<Card title={`Per-bank NPA (${byBank.length}) — sorted by NPA %`}>
				<div style={{ overflowX: 'auto' }}>
					<table className="tbl" style={{ width: '100%' }}>
						<thead>
							<tr>
								<SortableTh field="orgId" sort={bankSort} numeric>ID</SortableTh>
								<SortableTh field="orgName" sort={bankSort}>Bank</SortableTh>
								<SortableTh field="loanCr" sort={bankSort} numeric>Loan ₹Cr</SortableTh>
								<SortableTh field="npaCr" sort={bankSort} numeric>NPA ₹Cr</SortableTh>
								<SortableTh field="npaPct" sort={bankSort} numeric>NPA %</SortableTh>
								<SortableTh field="standardCr" sort={bankSort} numeric>Standard</SortableTh>
								<SortableTh field="subStandardCr" sort={bankSort} numeric>Sub-Std</SortableTh>
								<SortableTh field="doubtfulCr" sort={bankSort} numeric>Doubtful</SortableTh>
								<SortableTh field="lossCr" sort={bankSort} numeric>Loss</SortableTh>
								<th>Action</th>
							</tr>
						</thead>
						<tbody>
							{bankSort.rows.map((b) => (
								<tr
									key={b.orgId}
									className="clickable"
									onClick={() => navigate(`/banks/${b.orgId}`)}
									tabIndex={0}
								>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{b.orgId}</td>
									<td><BankBadge orgId={b.orgId} orgName={b.orgName} /></td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAbsCrore(b.loanCr)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAbsCrore(b.npaCr)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										<StatusPill flag={b.npaFlag} variant="npa" compact label={formatPct(b.npaPct)} />
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAbsCrore(b.standardCr)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAbsCrore(b.subStandardCr)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAbsCrore(b.doubtfulCr)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAbsCrore(b.lossCr)}</td>
									<td>
										<button
											type="button"
											className="btn ghost"
											onClick={(e) => {
												e.stopPropagation();
												navigate(`/banks/${b.orgId}`);
											}}
										>
											View
										</button>
									</td>
								</tr>
							))}
							{!bankSort.rows.length && (
								<tr><td colSpan={10}><EmptyState title="No banks" /></td></tr>
							)}
						</tbody>
					</table>
				</div>
			</Card>

			<Card title={`Top product NPA contributors (${byProduct.length})`}>
				<div style={{ overflowX: 'auto' }}>
					<table className="tbl" style={{ width: '100%' }}>
						<thead>
							<tr>
								<SortableTh field="productCode" sort={productSort}>Code</SortableTh>
								<SortableTh field="productName" sort={productSort}>Product</SortableTh>
								<SortableTh field="loanCr" sort={productSort} numeric>Loan ₹Cr</SortableTh>
								<SortableTh field="npaCr" sort={productSort} numeric>NPA ₹Cr</SortableTh>
								<SortableTh field="npaPct" sort={productSort} numeric>NPA %</SortableTh>
							</tr>
						</thead>
						<tbody>
							{productSort.rows.map((p) => (
								<tr
									key={p.productCode}
									className="clickable"
									onClick={() => navigate(`/products/${encodeURIComponent(p.productCode)}?type=LOAN`)}
									tabIndex={0}
								>
									<td style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{p.productCode}</td>
									<td>{p.productName}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAbsCrore(p.loanCr)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAbsCrore(p.npaCr)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										<StatusPill flag={npaFlag(p.npaPct)} variant="npa" compact label={formatPct(p.npaPct)} />
									</td>
								</tr>
							))}
							{!productSort.rows.length && (
								<tr><td colSpan={5}><EmptyState title="No products" /></td></tr>
							)}
						</tbody>
					</table>
				</div>
			</Card>
		</div>
	);
}
